'use client';

import { useState, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface DirectusCollection {
  collection: string;
  meta?: {
    note?: string | null;
    icon?: string | null;
    hidden?: boolean;
    singleton?: boolean;
  } | null;
}

interface DirectusItem {
  id?: string | number;
  [key: string]: unknown;
}

interface ConnectionStatus {
  connected: boolean;
  url: string;
  version?: string;
  error?: string;
}

export default function DirectusPage() {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [collections, setCollections] = useState<DirectusCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [items, setItems] = useState<DirectusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCollections = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/directus/collections');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to connect to Directus');
      setStatus({ connected: true, url: data.url, version: data.version });
      const userCollections = (data.collections as DirectusCollection[]).filter(
        (c) => !c.collection.startsWith('directus_') && !c.meta?.hidden
      );
      setCollections(userCollections);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to connect to Directus';
      setError(msg);
      setStatus({ connected: false, url: '', error: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  async function fetchItems(collection: string) {
    setItemsLoading(true);
    setError('');
    setItems([]);
    try {
      const res = await fetch(`/api/directus/items?collection=${encodeURIComponent(collection)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch items');
      setItems(data.items || []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch items';
      setError(msg);
    } finally {
      setItemsLoading(false);
    }
  }

  function handleSelectCollection(collection: string) {
    setSelectedCollection(collection);
    setSearchQuery('');
    fetchItems(collection);
  }

  const filteredCollections = collections.filter((c) =>
    c.collection.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedColl = collections.find((c) => c.collection === selectedCollection);

  function getItemKeys(items: DirectusItem[]): string[] {
    if (!items.length) return [];
    const keys = Object.keys(items[0]);
    return keys.slice(0, 6);
  }

  function renderValue(val: unknown): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'object') return JSON.stringify(val).slice(0, 60) + '...';
    return String(val).slice(0, 80);
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-4xl">🗄️</span>
              <h1 className="font-display text-4xl font-semibold text-foreground">Directus CMS</h1>
            </div>
            <p className="text-foreground-soft text-base">
              Browse your Directus collections and content.
            </p>
          </div>

          {/* Connection Status */}
          {loading ? (
            <div className="flex items-center gap-3 text-foreground-soft mb-8">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Connecting to Directus…</span>
            </div>
          ) : status?.connected ? (
            <div className="flex items-center gap-3 mb-8 bg-green-50 border border-green-200 rounded-xl px-5 py-3 w-fit">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
              <span className="text-sm font-medium text-green-800">
                Connected to <span className="font-semibold">{status.url}</span>
                {status.version && (
                  <span className="ml-2 text-green-600 font-normal">v{status.version}</span>
                )}
              </span>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-8 max-w-lg">
              <p className="text-sm font-semibold text-red-700 mb-1">Connection Failed</p>
              <p className="text-sm text-red-600">{error || 'Unable to connect to Directus.'}</p>
              <p className="text-xs text-red-500 mt-2">
                Ensure <code className="bg-red-100 px-1 rounded">DIRECTUS_URL</code> and{' '}
                <code className="bg-red-100 px-1 rounded">DIRECTUS_ACCESS_TOKEN</code> are set in
                your environment variables.
              </p>
            </div>
          )}

          {status?.connected && (
            <div className="flex gap-6">
              {/* Sidebar: Collections */}
              <aside className="w-64 flex-shrink-0">
                <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-border">
                    <h2 className="text-sm font-semibold text-foreground mb-2">Collections</h2>
                    <input
                      type="text"
                      placeholder="Search collections…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-foreground-soft focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="max-h-[520px] overflow-y-auto">
                    {filteredCollections.length === 0 ? (
                      <p className="text-xs text-foreground-soft px-4 py-6 text-center">
                        No collections found
                      </p>
                    ) : (
                      filteredCollections.map((c) => (
                        <button
                          key={c.collection}
                          onClick={() => handleSelectCollection(c.collection)}
                          className={`w-full text-left px-4 py-3 text-sm border-b border-border/50 transition-colors hover:bg-background ${
                            selectedCollection === c.collection
                              ? 'bg-primary/10 text-primary font-semibold' :'text-foreground'
                          }`}
                        >
                          <span className="mr-2">{c.meta?.icon ? '📁' : '📄'}</span>
                          {c.collection}
                          {c.meta?.singleton && (
                            <span className="ml-2 text-[10px] bg-surface border border-border rounded px-1 text-foreground-soft">
                              singleton
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-2 border-t border-border">
                    <p className="text-xs text-foreground-soft">
                      {collections.length} collection{collections.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </aside>

              {/* Main: Items */}
              <div className="flex-1 min-w-0">
                {!selectedCollection ? (
                  <div className="bg-surface border border-border rounded-2xl flex flex-col items-center justify-center py-20 text-center">
                    <span className="text-5xl mb-4">📂</span>
                    <p className="text-foreground font-medium mb-1">Select a collection</p>
                    <p className="text-sm text-foreground-soft">
                      Choose a collection from the sidebar to browse its items.
                    </p>
                  </div>
                ) : itemsLoading ? (
                  <div className="bg-surface border border-border rounded-2xl flex items-center justify-center py-20">
                    <div className="flex items-center gap-3 text-foreground-soft">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading items…</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 rounded-2xl px-6 py-8">
                    <p className="text-sm font-semibold text-red-700 mb-1">Error</p>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                ) : (
                  <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                      <div>
                        <h2 className="text-base font-semibold text-foreground">
                          {selectedColl?.collection}
                        </h2>
                        <p className="text-xs text-foreground-soft mt-0.5">
                          {items.length} item{items.length !== 1 ? 's' : ''}
                          {selectedColl?.meta?.note && ` · ${selectedColl.meta.note}`}
                        </p>
                      </div>
                      <a
                        href={`${status.url}/admin/content/${selectedCollection}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        Open in Directus ↗
                      </a>
                    </div>

                    {items.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <span className="text-4xl mb-3">📭</span>
                        <p className="text-foreground-soft text-sm">No items in this collection</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-background/50">
                              {getItemKeys(items).map((key) => (
                                <th
                                  key={key}
                                  className="px-4 py-3 text-left text-xs font-semibold text-foreground-soft uppercase tracking-wide"
                                >
                                  {key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr
                                key={item.id ?? idx}
                                className="border-b border-border/50 hover:bg-background/40 transition-colors"
                              >
                                {getItemKeys(items).map((key) => (
                                  <td
                                    key={key}
                                    className="px-4 py-3 text-foreground text-xs max-w-[200px] truncate"
                                    title={String(item[key] ?? '')}
                                  >
                                    {key === 'status' ? (
                                      <span
                                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                                          item[key] === 'published' ?'bg-green-50 text-green-700 border-green-200'
                                            : item[key] === 'draft' ?'bg-yellow-50 text-yellow-700 border-yellow-200' :'bg-surface text-foreground-soft border-border'
                                        }`}
                                      >
                                        {String(item[key])}
                                      </span>
                                    ) : (
                                      renderValue(item[key])
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
